/**
 * V68 인력 관리 — Tab 1: 인력투입 현황 (기준월 ±1개월, 총 3개월)
 *
 * V68-FIX: BE callable 은 ±2 (5개월) 반환하지만 FE 에서 가운데 3개월 (±1) 만 표시.
 *          데이터/API/BE 영향 0 — FE slice 만 적용. 컬럼 width 도 함께 조정.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useWorkforceAllocation } from '../../../shared/hooks/useWorkforceAllocation';
// V68: 고용형태 한글 표시 공용 formatter
import { formatEmploymentTypeName } from '../../../shared/utils/employmentTypeFormatter';

type CountryFilter = 'ALL' | 'KR' | 'CN';
const filterSelect: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--color-gray-300)', borderRadius: 6, fontSize: 13,
  background: 'white',
};

function getCurrentYearMonth(): string {
  const kst = new Date(new Date().getTime() + 9 * 60 * 60 * 1000);
  return `${kst.getUTCFullYear()}-${String(kst.getUTCMonth() + 1).padStart(2, '0')}`;
}

function addMonthsYm(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

const IDLE_STYLE: React.CSSProperties = { color: 'var(--color-orange-600)' };
const LEAVE_STYLE: React.CSSProperties = { color: 'var(--color-orange-600)', fontStyle: 'italic' };

// V68: BE 가 월별 entries 에 'leave' type 을 직접 포함 (LoA SSoT 기반 월 overlap).
//   row.status (현재 시점 단일 값) 의존 제거 — 휴직 종료된 월은 leave entry 없음.
function renderMonthCell(
  data: { entries: { type: 'idle' | 'project' | 'leave'; label: string }[] } | undefined,
): React.ReactNode {
  const entries = data?.entries || [];
  if (entries.length === 0) {
    return <span style={IDLE_STYLE}>유휴</span>;
  }
  return (
    <>
      {entries.map((en, i) => (
        <React.Fragment key={i}>
          {i > 0 && ', '}
          {en.type === 'idle' ? <span style={IDLE_STYLE}>{en.label}</span>
            : en.type === 'leave' ? <span style={LEAVE_STYLE}>{en.label}</span>
            : en.label}
        </React.Fragment>
      ))}
    </>
  );
}

const cellBase: React.CSSProperties = {
  padding: '8px 6px', borderBottom: '1px solid var(--color-gray-200)', verticalAlign: 'top',
  fontSize: '13px', whiteSpace: 'normal', wordBreak: 'keep-all', textAlign: 'center',
};
const headerCell: React.CSSProperties = {
  padding: '10px 6px', background: 'var(--color-gray-100)',
  borderBottom: '2px solid var(--color-gray-300)', fontWeight: 600, fontSize: '13px', textAlign: 'center',
};

export function WorkforceAllocationTab() {
  const currentYm = getCurrentYearMonth();
  const [baseYm, setBaseYm] = useState<string>(currentYm);
  const {
    data, months: allMonths, baseYearMonth: resolvedBase, scope,
    availableOrgs, availableEmploymentTypes, loading, error,
  } = useWorkforceAllocation(baseYm);

  // V68-FIX: BE 가 반환하는 5개월 중 가운데 3개월 (-1, base, +1) 만 표시
  // BE 반환 순서: [base-2, base-1, base, base+1, base+2] → slice(1, 4)
  const months = useMemo(() => allMonths.slice(1, 4), [allMonths]);

  // 필터 상태
  const [country, setCountry] = useState<CountryFilter>('ALL');
  const [orgUnitId, setOrgUnitId] = useState<string>('ALL');
  const [empType, setEmpType] = useState<string>('ALL');

  // 스코프 기반 디폴트 (canChooseAll=false → 단일 국가 강제)
  useEffect(() => {
    if (!scope.canChooseAll && scope.countries.length === 1) {
      setCountry(scope.countries[0]);
    } else if (scope.canChooseAll) {
      setCountry('ALL');
    }
  }, [scope.canChooseAll, scope.countries.join(',')]);

  // 국가 변경 시 조직 선택 reset (선택된 조직이 현재 국가에 없으면)
  useEffect(() => {
    if (orgUnitId === 'ALL') return;
    const org = availableOrgs.find(o => o.orgUnitId === orgUnitId);
    if (!org) { setOrgUnitId('ALL'); return; }
    if (country !== 'ALL' && org.companyCode !== country) setOrgUnitId('ALL');
  }, [country, orgUnitId, availableOrgs]);

  // 기준월 선택 옵션: ±6개월
  const baseOptions = useMemo(() => {
    const arr: string[] = [];
    for (let i = -6; i <= 6; i++) arr.push(addMonthsYm(currentYm, i));
    return arr;
  }, [currentYm]);

  // 국가 옵션 (스코프 제한)
  const countryOptions = useMemo<{ value: CountryFilter; label: string }[]>(() => {
    const opts: { value: CountryFilter; label: string }[] = [];
    if (scope.canChooseAll) opts.push({ value: 'ALL', label: 'ALL' });
    if (scope.countries.includes('KR')) opts.push({ value: 'KR', label: 'KR' });
    if (scope.countries.includes('CN')) opts.push({ value: 'CN', label: 'CN' });
    return opts;
  }, [scope.canChooseAll, scope.countries.join(',')]);

  // 조직 옵션 (국가 필터 종속)
  const orgOptions = useMemo(() => {
    return country === 'ALL'
      ? availableOrgs
      : availableOrgs.filter(o => o.companyCode === country);
  }, [country, availableOrgs]);

  // 클라이언트 필터링
  const filteredData = useMemo(() => {
    return data.filter(r => {
      if (country !== 'ALL' && r.companyCode !== country) return false;
      if (orgUnitId !== 'ALL' && r.orgUnitId !== orgUnitId) return false;
      if (empType !== 'ALL' && r.employmentTypeCode !== empType) return false;
      return true;
    });
  }, [data, country, orgUnitId, empType]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 14 }}>기준월:</label>
        <select value={baseYm} onChange={e => setBaseYm(e.target.value)} style={filterSelect}>
          {baseOptions.map(o => <option key={o} value={o}>{o.replace('-', '년 ')}월</option>)}
        </select>

        <label style={{ fontSize: 14, marginLeft: 8 }}>국가:</label>
        <select value={country} onChange={e => setCountry(e.target.value as CountryFilter)} style={filterSelect}>
          {countryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <label style={{ fontSize: 14, marginLeft: 8 }}>조직:</label>
        <select value={orgUnitId} onChange={e => setOrgUnitId(e.target.value)} style={filterSelect}>
          <option value="ALL">ALL</option>
          {orgOptions.map(o => (
            <option key={o.orgUnitId} value={o.orgUnitId}>
              {scope.canChooseAll ? `${o.name} (${o.companyCode})` : o.name}
            </option>
          ))}
        </select>

        <label style={{ fontSize: 14, marginLeft: 8 }}>고용형태:</label>
        <select value={empType} onChange={e => setEmpType(e.target.value)} style={filterSelect}>
          <option value="ALL">ALL</option>
          {availableEmploymentTypes.map(t => (
            <option key={t.code} value={t.code}>{formatEmploymentTypeName(t.code, t.name)}</option>
          ))}
        </select>

        <span style={{ fontSize: 12, color: 'var(--color-gray-500)', marginLeft: 4 }}>
          기준월 -1 ~ +1 (3개월)
        </span>
        <span style={{ fontSize: 13, color: 'var(--color-gray-600)', marginLeft: 'auto' }}>
          {filteredData.length}명 / 전체 {data.length}명
        </span>
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center' }}><Loader2 size={24} className="animate-spin" /></div>}
      {error && <div style={{ padding: 20, background: 'var(--color-red-50)', color: 'var(--color-red-700)', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      {!loading && !error && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1170, tableLayout: 'fixed' }}>
            <colgroup>
              {/* V68-FIX: 3-month layout. 130 + 90 + 110 + 280×3 = 1,170 (minWidth 동일 유지) */}
              <col style={{ width: 130 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 110 }} />
              {months.map(m => <col key={m.yearMonth} style={{ width: 280 }} />)}
            </colgroup>
            <thead>
              <tr>
                <th style={headerCell}>소속</th>
                <th style={{ ...headerCell, borderLeft: '1px solid var(--color-gray-200)' }}>이름</th>
                <th style={{ ...headerCell, borderLeft: '1px solid var(--color-gray-200)' }}>고용형태</th>
                {months.map(m => <th key={m.yearMonth} style={{
                  ...headerCell,
                  borderLeft: '1px solid var(--color-gray-200)',
                  ...(m.yearMonth === resolvedBase ? { background: 'var(--color-blue-100)', color: 'var(--color-blue-800)' } : {}),
                }}>{m.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {filteredData.map(row => {
                const orgTitle = `${row.orgUnitName} (${row.companyCode})`;
                return (
                  <tr key={row.userId} style={row.status === 'on_leave' ? { background: 'var(--color-gray-50)' } : undefined}>
                    <td style={{
                      ...cellBase,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={orgTitle}>
                      {row.orgUnitName}
                      <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--color-gray-500)' }}>({row.companyCode})</span>
                    </td>
                    <td style={{ ...cellBase, borderLeft: '1px solid var(--color-gray-200)' }}>
                      {row.nameKo}
                      {row.status === 'on_leave' && (
                        <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--color-orange-600)' }}>(휴직)</span>
                      )}
                    </td>
                    <td style={{ ...cellBase, borderLeft: '1px solid var(--color-gray-200)' }}>{formatEmploymentTypeName(row.employmentTypeCode, row.employmentTypeName)}</td>
                    {months.map(m => (
                      <td key={m.yearMonth} style={{
                        ...cellBase,
                        borderLeft: '1px solid var(--color-gray-200)',
                      }}>
                        {renderMonthCell(row.months?.[m.yearMonth])}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {filteredData.length === 0 && (
                <tr><td colSpan={3 + months.length} style={{ ...cellBase, textAlign: 'center', padding: 40, color: 'var(--color-gray-500)' }}>
                  데이터가 없습니다 (기준월={resolvedBase})
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
